/**
 * 15-part Parametric SVG Skeletal Character Rig with Forward Kinematics.
 */
import { Renderer } from '../core/Renderer';
import { VisemeType, LipSync } from './LipSync';
import { FacialState } from './ExpressionController';

export interface RigColors {
  skin: string;
  hair: string;
  shirt: string;
  pant: string;
  accent: string;
}

export interface RigGeometry {
  headRadiusX?: number;
  headRadiusY?: number;
  torsoWidth?: number;
  torsoHeight?: number;
  armWidth?: number;
  armLength?: number;
  legWidth?: number;
  legLength?: number;
  footRx?: number;
  footRy?: number;
  shoeColor?: string;
}

export interface BoneAngles {
  bodyTilt: number; // degrees
  headAngle: number;
  leftUpperArm: number;
  leftLowerArm: number;
  rightUpperArm: number;
  rightLowerArm: number;
  leftUpperLeg: number;
  leftLowerLeg: number;
  rightUpperLeg: number;
  rightLowerLeg: number;
  breathingScaleY: number;
}

export class CharacterRig {
  public root: SVGGElement;
  public colors: RigColors;
  public geometry: RigGeometry;
  public accessories: { glasses?: boolean; labCoat?: boolean; crown?: boolean; tie?: boolean; cap?: boolean } = {};

  // Part Nodes
  private headGroup: SVGGElement;
  private mouthPath: SVGPathElement;
  private eyeLeft: SVGGElement;
  private eyeRight: SVGGElement;
  private pupilLeft: SVGCircleElement;
  private pupilRight: SVGCircleElement;
  private eyebrowLeft: SVGPathElement;
  private eyebrowRight: SVGPathElement;
  private blushLeft: SVGCircleElement;
  private blushRight: SVGCircleElement;

  private bodyGroup: SVGGElement;
  private leftArmGroup: SVGGElement;
  private leftForearmGroup: SVGGElement;
  private rightArmGroup: SVGGElement;
  private rightForearmGroup: SVGGElement;

  private leftLegGroup: SVGGElement;
  private leftCalfGroup: SVGGElement;
  private rightLegGroup: SVGGElement;
  private rightCalfGroup: SVGGElement;

  constructor(
    colors?: Partial<RigColors>,
    accessories?: { glasses?: boolean; labCoat?: boolean; crown?: boolean; tie?: boolean; cap?: boolean },
    geometry?: Partial<RigGeometry>
  ) {
    this.colors = {
      skin: '#fcd34d',
      hair: '#1e293b',
      shirt: '#0284c7',
      pant: '#334155',
      accent: '#fbbf24',
      ...colors
    };
    this.accessories = accessories || {};
    this.geometry = {
      headRadiusX: 34,
      headRadiusY: 40,
      torsoWidth: 70,
      torsoHeight: 100,
      armWidth: 16,
      armLength: 48,
      legWidth: 20,
      legLength: 50,
      footRx: 16,
      footRy: 8,
      shoeColor: '#0f172a',
      ...geometry
    };

    this.root = Renderer.createElement('g');
    this.root.classList.add('asae-character-rig');

    // Build SVG Hierarchy
    // 1. Legs behind body
    this.leftLegGroup = Renderer.createElement('g');
    this.leftCalfGroup = Renderer.createElement('g');
    this.rightLegGroup = Renderer.createElement('g');
    this.rightCalfGroup = Renderer.createElement('g');

    // 2. Body & Neck
    this.bodyGroup = Renderer.createElement('g');

    // 3. Arms
    this.leftArmGroup = Renderer.createElement('g');
    this.leftForearmGroup = Renderer.createElement('g');
    this.rightArmGroup = Renderer.createElement('g');
    this.rightForearmGroup = Renderer.createElement('g');

    // 4. Head, Face & Features
    this.headGroup = Renderer.createElement('g');
    this.mouthPath = Renderer.createElement('path');
    this.eyeLeft = Renderer.createElement('g');
    this.eyeRight = Renderer.createElement('g');
    this.pupilLeft = Renderer.createElement('circle');
    this.pupilRight = Renderer.createElement('circle');
    this.eyebrowLeft = Renderer.createElement('path');
    this.eyebrowRight = Renderer.createElement('path');
    this.blushLeft = Renderer.createElement('circle');
    this.blushRight = Renderer.createElement('circle');

    this.assembleSkeletalHierarchy();
  }

  private assembleSkeletalHierarchy(): void {
    // 1. Legs assembly
    // Left Leg
    const lThigh = Renderer.createElement('rect');
    Renderer.setAttributes(lThigh, { x: -10, y: 0, width: 20, height: 50, rx: 8, ry: 8, fill: this.colors.pant });
    this.leftLegGroup.appendChild(lThigh);
    const lCalf = Renderer.createElement('rect');
    Renderer.setAttributes(lCalf, { x: -8, y: 0, width: 16, height: 55, rx: 6, ry: 6, fill: this.colors.pant });
    const lFoot = Renderer.createElement('ellipse');
    Renderer.setAttributes(lFoot, { cx: 8, cy: 52, rx: 16, ry: 8, fill: '#0f172a' });
    this.leftCalfGroup.appendChild(lCalf);
    this.leftCalfGroup.appendChild(lFoot);
    this.leftLegGroup.appendChild(this.leftCalfGroup);

    // Right Leg
    const rThigh = Renderer.createElement('rect');
    Renderer.setAttributes(rThigh, { x: -10, y: 0, width: 20, height: 50, rx: 8, ry: 8, fill: this.colors.pant });
    this.rightLegGroup.appendChild(rThigh);
    const rCalf = Renderer.createElement('rect');
    Renderer.setAttributes(rCalf, { x: -8, y: 0, width: 16, height: 55, rx: 6, ry: 6, fill: this.colors.pant });
    const rFoot = Renderer.createElement('ellipse');
    Renderer.setAttributes(rFoot, { cx: 8, cy: 52, rx: 16, ry: 8, fill: '#0f172a' });
    this.rightCalfGroup.appendChild(rCalf);
    this.rightCalfGroup.appendChild(rFoot);
    this.rightLegGroup.appendChild(this.rightCalfGroup);

    this.root.appendChild(this.leftLegGroup);
    this.root.appendChild(this.rightLegGroup);

    // 2. Torso / Body
    const torso = Renderer.createElement('rect');
    Renderer.setAttributes(torso, {
      x: -35,
      y: -100,
      width: 70,
      height: 100,
      rx: 16,
      ry: 16,
      fill: this.colors.shirt,
      stroke: '#0369a1',
      'stroke-width': 1.5
    });
    this.bodyGroup.appendChild(torso);

    if (this.accessories.tie) {
      const tie = Renderer.createElement('polygon');
      Renderer.setAttributes(tie, { points: '-6,-90 6,-90 10,-30 0,-15 -10,-30', fill: '#ef4444' });
      this.bodyGroup.appendChild(tie);
    }
    if (this.accessories.labCoat) {
      const coatL = Renderer.createElement('path');
      Renderer.setAttributes(coatL, { d: 'M -35 -100 L -12 -100 L -20 0 L -35 0 Z', fill: '#ffffff', opacity: '0.9' });
      const coatR = Renderer.createElement('path');
      Renderer.setAttributes(coatR, { d: 'M 35 -100 L 12 -100 L 20 0 L 35 0 Z', fill: '#ffffff', opacity: '0.9' });
      this.bodyGroup.appendChild(coatL);
      this.bodyGroup.appendChild(coatR);
    }

    // Neck
    const neck = Renderer.createElement('rect');
    Renderer.setAttributes(neck, { x: -10, y: -118, width: 20, height: 22, rx: 4, ry: 4, fill: this.colors.skin });
    this.bodyGroup.appendChild(neck);
    this.root.appendChild(this.bodyGroup);

    // 3. Left Arm (shoulder at (-35, -90))
    const lBicep = Renderer.createElement('rect');
    Renderer.setAttributes(lBicep, { x: -8, y: 0, width: 16, height: 48, rx: 8, ry: 8, fill: this.colors.shirt });
    this.leftArmGroup.appendChild(lBicep);
    const lForearm = Renderer.createElement('rect');
    Renderer.setAttributes(lForearm, { x: -7, y: 0, width: 14, height: 46, rx: 6, ry: 6, fill: this.colors.skin });
    const lPalm = Renderer.createElement('circle');
    Renderer.setAttributes(lPalm, { cx: 0, cy: 48, r: 10, fill: this.colors.skin });
    this.leftForearmGroup.appendChild(lForearm);
    this.leftForearmGroup.appendChild(lPalm);
    this.leftArmGroup.appendChild(this.leftForearmGroup);
    this.root.appendChild(this.leftArmGroup);

    // 4. Right Arm (shoulder at (35, -90))
    const rBicep = Renderer.createElement('rect');
    Renderer.setAttributes(rBicep, { x: -8, y: 0, width: 16, height: 48, rx: 8, ry: 8, fill: this.colors.shirt });
    this.rightArmGroup.appendChild(rBicep);
    const rForearm = Renderer.createElement('rect');
    Renderer.setAttributes(rForearm, { x: -7, y: 0, width: 14, height: 46, rx: 6, ry: 6, fill: this.colors.skin });
    const rPalm = Renderer.createElement('circle');
    Renderer.setAttributes(rPalm, { cx: 0, cy: 48, r: 10, fill: this.colors.skin });
    this.rightForearmGroup.appendChild(rForearm);
    this.rightForearmGroup.appendChild(rPalm);
    this.rightArmGroup.appendChild(this.rightForearmGroup);
    this.root.appendChild(this.rightArmGroup);

    // 5. Head Assembly (pivot at (0, -115))
    // Head shape
    const headShape = Renderer.createElement('ellipse');
    Renderer.setAttributes(headShape, { cx: 0, cy: -40, rx: 34, ry: 40, fill: this.colors.skin });
    this.headGroup.appendChild(headShape);

    // Hair
    const hair = Renderer.createElement('path');
    Renderer.setAttributes(hair, {
      d: 'M -34 -45 Q -38 -80 0 -82 Q 38 -80 34 -45 Q 36 -65 0 -68 Q -36 -65 -34 -45 Z',
      fill: this.colors.hair
    });
    this.headGroup.appendChild(hair);

    if (this.accessories.crown) {
      const crown = Renderer.createElement('polygon');
      Renderer.setAttributes(crown, {
        points: '-26,-80 -18,-96 -8,-84 0,-100 8,-84 18,-96 26,-80',
        fill: '#fbbf24',
        stroke: '#d97706',
        'stroke-width': 1.5
      });
      this.headGroup.appendChild(crown);
    }

    if (this.accessories.cap) {
      const cap = Renderer.createElement('polygon');
      Renderer.setAttributes(cap, {
        points: '-36,-75 0,-92 36,-75 0,-62',
        fill: '#1e293b',
        stroke: '#0f172a',
        'stroke-width': 1.5
      });
      const capSkull = Renderer.createElement('ellipse');
      Renderer.setAttributes(capSkull, { cx: 0, cy: -70, rx: 18, ry: 8, fill: '#0f172a' });
      const tassel = Renderer.createElement('line');
      Renderer.setAttributes(tassel, { x1: 0, y1: -77, x2: 24, y2: -60, stroke: '#fbbf24', 'stroke-width': 2 });
      this.headGroup.appendChild(capSkull);
      this.headGroup.appendChild(cap);
      this.headGroup.appendChild(tassel);
    }

    // Eyes
    this.setupEye(this.eyeLeft, this.pupilLeft, -14, -42);
    this.setupEye(this.eyeRight, this.pupilRight, 14, -42);
    this.headGroup.appendChild(this.eyeLeft);
    this.headGroup.appendChild(this.eyeRight);

    if (this.accessories.glasses) {
      const gL = Renderer.createElement('circle');
      Renderer.setAttributes(gL, { cx: -14, cy: -42, r: 12, fill: 'none', stroke: '#1e293b', 'stroke-width': 2 });
      const gR = Renderer.createElement('circle');
      Renderer.setAttributes(gR, { cx: 14, cy: -42, r: 12, fill: 'none', stroke: '#1e293b', 'stroke-width': 2 });
      const bridge = Renderer.createElement('line');
      Renderer.setAttributes(bridge, { x1: -2, y1: -42, x2: 2, y2: -42, stroke: '#1e293b', 'stroke-width': 2 });
      this.headGroup.appendChild(gL);
      this.headGroup.appendChild(gR);
      this.headGroup.appendChild(bridge);
    }

    // Eyebrows
    Renderer.setAttributes(this.eyebrowLeft, { d: 'M -22 -54 Q -14 -56 -6 -54', fill: 'none', stroke: this.colors.hair, 'stroke-width': 2.5 });
    Renderer.setAttributes(this.eyebrowRight, { d: 'M 6 -54 Q 14 -56 22 -54', fill: 'none', stroke: this.colors.hair, 'stroke-width': 2.5 });
    this.headGroup.appendChild(this.eyebrowLeft);
    this.headGroup.appendChild(this.eyebrowRight);

    // Blushes
    Renderer.setAttributes(this.blushLeft, { cx: -20, cy: -32, r: 7, fill: '#f43f5e', opacity: '0' });
    Renderer.setAttributes(this.blushRight, { cx: 20, cy: -32, r: 7, fill: '#f43f5e', opacity: '0' });
    this.headGroup.appendChild(this.blushLeft);
    this.headGroup.appendChild(this.blushRight);

    // Mouth
    Renderer.setAttributes(this.mouthPath, {
      d: LipSync.VISEMES.rest.d,
      transform: 'translate(0, -22)',
      fill: '#be185d',
      stroke: '#881337',
      'stroke-width': 1.5
    });
    this.headGroup.appendChild(this.mouthPath);

    this.root.appendChild(this.headGroup);
  }

  private setupEye(eyeGroup: SVGGElement, pupil: SVGCircleElement, x: number, y: number): void {
    Renderer.setAttributes(eyeGroup, { transform: `translate(${x}, ${y})` });
    const sclera = Renderer.createElement('ellipse');
    Renderer.setAttributes(sclera, { cx: 0, cy: 0, rx: 7, ry: 7, fill: '#ffffff', stroke: '#cbd5e1', 'stroke-width': 1 });
    eyeGroup.appendChild(sclera);

    Renderer.setAttributes(pupil, { cx: 0, cy: 0, r: 4, fill: '#0f172a' });
    eyeGroup.appendChild(pupil);

    const glint = Renderer.createElement('circle');
    Renderer.setAttributes(glint, { cx: -1.5, cy: -1.5, r: 1.5, fill: '#ffffff' });
    eyeGroup.appendChild(glint);
  }

  public setViseme(viseme: VisemeType): void {
    const shape = LipSync.VISEMES[viseme] || LipSync.VISEMES.rest;
    this.mouthPath.setAttribute('d', shape.d);
    if (shape.fill) this.mouthPath.setAttribute('fill', shape.fill);
  }

  public applyFacialState(face: FacialState): void {
    // Eyebrows
    this.eyebrowLeft.setAttribute(
      'd',
      `M -22 ${-54 + face.eyebrowY} Q -14 ${-56 + face.eyebrowY - face.eyebrowAngleLeft * 0.2} -6 ${-54 + face.eyebrowY}`
    );
    this.eyebrowRight.setAttribute(
      'd',
      `M 6 ${-54 + face.eyebrowY} Q 14 ${-56 + face.eyebrowY - face.eyebrowAngleRight * 0.2} 22 ${-54 + face.eyebrowY}`
    );

    // Eye scale (blink / open)
    this.eyeLeft.setAttribute('transform', `translate(-14, -42) scale(1, ${face.eyeScaleY.toFixed(2)})`);
    this.eyeRight.setAttribute('transform', `translate(14, -42) scale(1, ${face.eyeScaleY.toFixed(2)})`);

    // Pupils gaze
    this.pupilLeft.setAttribute('cx', face.pupilOffsetX.toFixed(1));
    this.pupilLeft.setAttribute('cy', face.pupilOffsetY.toFixed(1));
    this.pupilRight.setAttribute('cx', face.pupilOffsetX.toFixed(1));
    this.pupilRight.setAttribute('cy', face.pupilOffsetY.toFixed(1));

    // Blush
    this.blushLeft.setAttribute('opacity', face.blush ? '0.45' : '0');
    this.blushRight.setAttribute('opacity', face.blush ? '0.45' : '0');
  }

  public setBoneAngles(bones: Partial<BoneAngles>): void {
    // 1. Legs (hip pivots at (-16, 0) and (16, 0))
    const lUL = bones.leftUpperLeg ?? 0;
    const lLL = bones.leftLowerLeg ?? 0;
    Renderer.setAttributes(this.leftLegGroup, { transform: `translate(-16, 0) rotate(${lUL})` });
    Renderer.setAttributes(this.leftCalfGroup, { transform: `translate(0, 48) rotate(${lLL})` });

    const rUL = bones.rightUpperLeg ?? 0;
    const rLL = bones.rightLowerLeg ?? 0;
    Renderer.setAttributes(this.rightLegGroup, { transform: `translate(16, 0) rotate(${rUL})` });
    Renderer.setAttributes(this.rightCalfGroup, { transform: `translate(0, 48) rotate(${rLL})` });

    // 2. Torso
    const tilt = bones.bodyTilt ?? 0;
    const breath = bones.breathingScaleY ?? 1;
    Renderer.setAttributes(this.bodyGroup, { transform: `rotate(${tilt}) scale(1, ${breath.toFixed(3)})` });

    // 3. Arms (shoulder pivots at (-35, -90) and (35, -90))
    const lUA = bones.leftUpperArm ?? 15;
    const lLA = bones.leftLowerArm ?? 10;
    Renderer.setAttributes(this.leftArmGroup, { transform: `translate(-35, -90) rotate(${lUA})` });
    Renderer.setAttributes(this.leftForearmGroup, { transform: `translate(0, 46) rotate(${lLA})` });

    const rUA = bones.rightUpperArm ?? -15;
    const rLA = bones.rightLowerArm ?? -10;
    Renderer.setAttributes(this.rightArmGroup, { transform: `translate(35, -90) rotate(${rUA})` });
    Renderer.setAttributes(this.rightForearmGroup, { transform: `translate(0, 46) rotate(${rLA})` });

    // 4. Head
    const hAngle = bones.headAngle ?? 0;
    Renderer.setAttributes(this.headGroup, { transform: `translate(0, -115) rotate(${hAngle})` });
  }
}
